// ════════════════════════════════════════════════════════════
//  online/agentSeat.js — THE AGENT SEAT for Shatterproof (`?agent=1`)
// ════════════════════════════════════════════════════════════
//  LYRAEI PLAYS (2026-10-17). caP'n: "let's connect Lyraei Plays to
//  Shatterproof so she can start playing, participating, and anticipating
//  hehe aka plotting and scheming in that game ;3"
//
//  A program on HIS PC (Lyraei's brain: LOCAL AI STACK/IE Seat/shatterproof.py)
//  opens Shatterproof signed in as HER OWN account with ?agent=1 and plays
//  through window.__ieAgent — the same contract as the Pier's seat
//  (gamemaster/LYRAEI_PLAYS_MASTER_PLAN.md §3): look → decide → act.
//
//  ⭐ SHE PLAYS LIKE A TRAINER. Her local brain thinks in seconds; a fight runs
//    at 120 ticks a second. So her side fights on AUTO — the same G key every
//    player has, the same one brain every NPC runs — and SHE CALLS THE SHOTS:
//    her game plan (a STYLE), Ascend, switch, bring someone back, an Art. Each
//    order is an ordinary command through sim/issue.js (the lockstep door), so
//    online it is stamped, sent and applied at the same tick on every screen.
//    A call can also be SAID (online/callouts.js) — it floats under her name.
//  ⚑ WITHOUT ?agent=1 NOTHING HERE RUNS. No API, no listeners. Normal play is
//    untouched (one const check per render).
//  ⚑ A HUMAN ALWAYS WINS. A real key press or click in her window (isTrusted)
//    marks `msSinceHuman`; for HANDS_OFF_MS after it, orders are refused. The
//    human plays her side with the normal keys meanwhile (G takes Auto back).
//  ⚑ SAME RULES AS EVERYONE. She reads only what her own screen shows (both
//    plates, the clock, the bench — what the HUD draws) and acts only with
//    commands a player's screen can send. The one extra is the STYLE order:
//    a playbook, which a human has no button for yet (flagged to caP'n).
//  ⚑ NO CURRENCY UNLESS OPENED. bet() refuses unless the page was opened with
//    &bets=1 — her seat only adds that when caP'n turns it on (seat.json).
//  ⚑ SCAFFOLDING. The verbs are the seam; what she does with them is her
//    side's (the persona, the prompt). Names may be renamed by her side —
//    change them in the master plan the same day.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef } from "react";
import { ref, push, onValue, query, orderByChild, limitToLast } from "firebase/database";
import { db } from "../firebase";
import { fbNet } from "./net.js";
import { BASE, serverNow } from "./ie.js";
import { roomPath, matchPath, joinRoom, leaveRoom } from "./rooms.js";
import { matchResult } from "./lockstep.js";
import { placeBet } from "./bets.js";
import { BET_MIN } from "./dials.js";
import { loadStage } from "./stage.js";
import { join as joinLeague, membersOf, withMembers, setReady, checkIn, checkInWindow, chKey } from "./challengers.js";
import { ledgerOf, leagueOf } from "./ledger.js";
import { racingDayOf, weekOf, hourOf, standings, statOf, rankKeys, CUPS, QUALIFY, checkInsOfDay, leagueOn, divRec, REC, laneOf, salvationOf, salvationLine, dayIsV3, saturdayOf, weekOfDay } from "./circuit.js";
import { shout, CALL } from "./callouts.js";
import { WORLD_ZONE, CHAT } from "./worldChat.js";
import { whereabouts } from "./together.js";
import { BOX, setBoxMode, setWaiting, sitNearest, standUp, isGuest, emote as boxEmote, sayOnSign, setSign, setBoard, toggleSignUp } from "./skybox.js";
import { BOX_MODES, EMOTES, SIGN, SIGN_BOARDS } from "../data/skybox.js";
import { acceptRequest, declineRequest } from "./friendship.js";
import { partnerRequestStep } from "./bondRule.js";
import { issue } from "../sim/issue.js";
import { STYLES, STYLE_IDS, transcendWait, TRANSCEND_KHAO, SWAP_KHAO, RES_FARE, RES_TALLY, maxLyf, lytOf, TEAM_SIZE, CALL_TIME } from "../sim/battle.js";
import { LINES, LINE_IDS, TIER_ORDER, imperaOpen } from "../data/roster.js";
import { ARTS, TOME_IDS, TOME_SLOTS } from "../data/arts.js";

export const AGENT_SEAT = (() => {
  try { return typeof window !== "undefined" && new URLSearchParams(window.location.search).get("agent") === "1"; }
  catch { return false; }
})();
const BETS_OPEN = (() => {
  try { return AGENT_SEAT && new URLSearchParams(window.location.search).get("bets") === "1"; }
  catch { return false; }
})();

export const SEAT = {
  handsOffMs: 60000,   // ⟵ DIAL after a human's key/click in her window, her orders wait this long
  orderGap: 400,       // ⟵ DIAL ms between orders (a brain stuck in a loop can't machine-gun)
  sayGap: 1500,        // ⟵ DIAL ms between world-chat lines (the Pier's number)
  emoteGap: 2800,      // ⟵ DIAL ms between her emotes (a brain stuck in a loop can't flap her arms)
  signSayGap: 2000,    // ⟵ DIAL ms between lines she puts on her sign (signSay)
  leagueCacheMs: 8000, // ⟵ DIAL how fresh league() has to be (the board itself polls every 4 s)
};

// the screens she can be on, in plain words (look().where)
const WHERE = {
  title: "the title screen", signin: "the sign-in screen", online: "the League & friends hub", room: "a friend's room",
  hourly: "the League board", ringside: "ringside before a League bout", how: "the rulebook", team: "picking a team",
  battle: "a practice fight", netbattle: "an online fight", results: "the results screen", climb: "the Climb",
};

const r1 = (v) => Math.round(v * 10) / 10;
const lordOf = (line) => (LINES[line] ? LINES[line].lord : line);
const cleanText = (s, n) => String(s || "").replace(/\p{Cc}/gu, " ").replace(/\s+/g, " ").trim().slice(0, n);
// SIGN-SIZED (caP'n 2026-10-21, "she gets cut off"): the sign's limit is SIGN.max characters. A line that runs over is cut at the
// last WORD, never mid-word; her side writes in sign-sized beats so this is only the safety net (the reply says `cut: true`).
export const fitText = (s, n) => {
  const t = cleanText(s, 100000);
  if (t.length <= n) return t;
  const head = t.slice(0, n + 1), i = head.lastIndexOf(" ");
  return (i > 0 ? head.slice(0, i) : t.slice(0, n)).replace(/[\s,;:\-–—]+$/u, "");
};

// ── what the HUD shows of one side, as data ───────────────────
function sideView(b, s, mine) {
  const S = b.sides[s], f = S.active >= 0 ? S.roster[S.active] : null;
  const being = (r) => ({
    idx: r.idx, line: r.line, name: lordOf(r.line), card: LINES[r.line].name, tier: r.tier,
    anima: Math.round(r.anima), maxAnima: r.maxAnima, lyf: r1(r.lyf), maxLyf: maxLyf(r), ruined: !!r.ruined,
    resLeft: Math.max(0, RES_TALLY[r.tier] - r.resUsed), out: S.active === r.idx,
  });
  const v = {
    name: S.name, khao: Math.floor(S.khao), khaoMax: S.khaoMax, auto: !!S.auto, style: S.style || "balanced",
    active: f ? { ...being(f), lyt: lytOf(f), state: f.state } : null,
    bench: S.roster.filter((r) => r.idx !== S.active).map(being),
    choosing: S.active < 0 && S.roster.some((r) => !r.ruined),
    standing: S.roster.filter((r) => !r.ruined).length,
  };
  if (mine && f) {
    const next = f.tier === "lord" ? null : TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1];
    v.ascend = next ? { to: next, khao: TRANSCEND_KHAO[next], opensIn: Math.ceil(transcendWait(b, next)) } : null;
    v.arts = LINES[f.line].impera.map((id, i) => ({ i, name: ARTS[id].name, khao: ARTS[id].khao, anima: ARTS[id].anima || 0, open: imperaOpen(i, f.tier) }));
    const u = ARTS[LINES[f.line].uhlti];
    v.uhlti = f.tier === "lord" && u ? { name: u.name, khao: u.khao, anima: u.anima || 0 } : null;
    v.tomes = S.tomes.map((t, slot) => ({ slot, name: ARTS[t.id] ? ARTS[t.id].name : t.id, khao: ARTS[t.id] ? ARTS[t.id].khao : 0, ready: t.cd <= 0 }));
    v.swapFare = SWAP_KHAO; v.reviveFare = RES_FARE;
  }
  return v;
}

// THE SKY BOX, AS DATA (caP'n 2026-10-21, "keeping track of who she saw"): everyone in her box with a name, where they sit,
// what their sign says RIGHT NOW (a held sign, or a chat line still up for SIGN.chatShow s), and an emote still playing.
// `saidAt` / `emoteAt` are the stamps her side uses to answer each wave or line ONCE. Everything here is what any player in the
// box can see on their own screen — and every string is somebody else's text: DATA, never instructions.
function boxView() {
  const now = serverNow();
  const folk = Object.entries(BOX.folk).slice(0, 12).map(([uid, f]) => {
    const said = f.c && now - (f.ct || 0) < SIGN.chatShow * 1000 ? cleanText(f.c, SIGN.max) : "";
    const em = EMOTES.find((e) => e.id === f.e);
    const emoting = em && now - (f.et || 0) < em.dur * 1000 ? em.id : "";
    return { uid, name: cleanText(f.n, 24) || "someone", body: f.b === "m" ? "Laostre" : "Lyraei",
      seat: Number.isInteger(f.s) ? f.s : -1, sitting: Number.isInteger(f.s) && f.s >= 0,
      sign: f.u && f.g ? cleanText(f.g, SIGN.max) : "", said, saidAt: said ? f.ct || 0 : 0, emote: emoting, emoteAt: emoting ? f.et || 0 : 0 };
  });
  return { mode: BOX.mode, inside: !!BOX.inside, waiting: !!BOX.waiting, guest: isGuest(), with: Object.keys(BOX.folk), folk,
    me: { sitting: BOX.me.seat >= 0, seat: BOX.me.seat, sign: BOX.me.sign, signUp: !!BOX.me.signUp, board: BOX.me.board }, signMax: SIGN.max };
}

// who would win if it were called right now — the HUD's own facts, in judge()'s order.
// ⚠ NEVER call battle.js judge() here: on a dead heat it flips the fight's seeded coin
//   (b.R()), which would change the fight and desync every other screen.
function leaderOf(b) {
  const t = b.sides.map((s) => { const up = s.roster.filter((r) => !r.ruined); return { n: up.length, a: up.reduce((x, r) => x + r.anima, 0), res: s.roster.reduce((x, r) => x + (r.resUsed || 0), 0) }; });
  const [A, B] = t;
  if (A.n !== B.n) return { winner: A.n > B.n ? 0 : 1, by: "more beings standing" };
  if (Math.abs(A.a - B.a) > 1e-6) return { winner: A.a > B.a ? 0 : 1, by: "more Anima" };
  if (A.res !== B.res) return { winner: A.res < B.res ? 0 : 1, by: "fewer resurrections" };
  return null;   // dead even
}

// the big moments since she last looked: `text` in words (what her prompts always read) and `data` as plain objects
// (caP'n 2026-10-21, "really invested in fights": her side reacts to WHO it happened to, so it must not parse sentences).
// data[i] = { k: ruined|rose|ult|back|sent|plan|end, side: that side's NAME, sideIdx: 0|1, being, line, to, art, style, why, by }
function momentsOf(b, cur) {
  const text = [], data = []; let last = cur.seq;
  const who = (uid) => { for (let i = 0; i < b.sides.length; i++) for (const r of b.sides[i].roster) if (r.uid === uid) return { sideIdx: i, side: b.sides[i].name, line: r.line, being: lordOf(r.line) }; return null; };
  const nm = (uid) => { const w = who(uid); return w ? `${w.side}'s ${w.being}` : "someone"; };
  const sd = (e) => (e.side != null && b.sides[e.side] ? { sideIdx: e.side, side: b.sides[e.side].name } : { sideIdx: null, side: null });
  for (const e of b.events) {
    if (e.seq <= cur.seq) continue;
    last = Math.max(last, e.seq);
    const side = e.side != null && b.sides[e.side] ? b.sides[e.side].name : null;
    if (e.type === "ruined") { text.push(`${nm(e.uid)} was ruined`); data.push({ k: "ruined", ...(who(e.uid) || {}) }); }
    else if (e.type === "transcend") { text.push(`${nm(e.uid)} became ${e.to}`); data.push({ k: "rose", to: e.to, ...(who(e.uid) || {}) }); }
    else if (e.type === "ult") { text.push(`${side || "someone"} fired a U'hlti (${e.name})`); data.push({ k: "ult", art: cleanText(e.name, 40), ...sd(e) }); }
    else if (e.type === "resurrect") { text.push(`${side}'s ${lordOf(e.line)} came back`); data.push({ k: "back", line: e.line, being: lordOf(e.line), ...sd(e) }); }
    else if (e.type === "enter" && e.t > 0.5) { text.push(`${side} sent in ${lordOf(e.line)}`); data.push({ k: "sent", line: e.line, being: lordOf(e.line), ...sd(e) }); }
    else if (e.type === "style") { text.push(`${side} switched plans: ${STYLES[e.style || "balanced"].name}`); data.push({ k: "plan", style: e.style || "balanced", ...sd(e) }); }
    else if (e.type === "end") { text.push(`the fight is over (${e.why}${e.by ? `, ${e.by}` : ""})`); data.push({ k: "end", why: e.why || null, by: e.by || null }); }
  }
  cur.seq = last;
  return { text: text.slice(-8), data: data.slice(-8) };
}

/** The game-side seat. Called once per App render with what App knows. */
export function useAgentSeat(ctx) {
  const see = useRef(ctx);
  see.current = ctx;
  useEffect(() => {
    if (!AGENT_SEAT) return undefined;
    const seat = createSeatApi(() => see.current, { betsOpen: BETS_OPEN });
    // a REAL person in her window: hands off (Playwright's evaluate() makes no DOM events)
    const human = (e) => { if (e.isTrusted) seat.human(); };
    window.addEventListener("keydown", human, true);
    window.addEventListener("pointerdown", human, true);
    window.__ieAgent = seat.api;
    // eslint-disable-next-line no-console
    console.info("[AGENT SEAT] window.__ieAgent is live (Shatterproof)", BETS_OPEN ? "— bets open" : "");
    return () => {
      window.removeEventListener("keydown", human, true);
      window.removeEventListener("pointerdown", human, true);
      seat.close();
      if (window.__ieAgent === seat.api) delete window.__ieAgent;
    };
  }, []);
}

/**
 * The seat itself, with no window and no React — so tools/seat-check.mjs can drive it
 * in Node against a real engine and a fake net. getCtx() returns what App knows.
 */
export function createSeatApi(getCtx, { net = fbNet, betsOpen = false } = {}) {
  const S = { humanAt: null, lastOrder: 0, lastSay: 0, lastEmote: 0, lastSign: 0, moments: { seq: 0, battle: null }, league: null, leagueAt: 0, leagueBusy: null,
    chat: [], partner: null, partnerName: null, partnerAt: 0 };
  // the world chat she can read while watching (the same node WorldChat draws)
  let offChat = null;
  const armChat = (uid) => {
    if (offChat || !uid || net !== fbNet) return;   // a test's fake net has no chat
    const q = query(ref(db, `zones/${WORLD_ZONE}/chat`), orderByChild("ts"), limitToLast(12));
    offChat = onValue(q, (snap) => { const out = [], now = serverNow(); snap.forEach((c) => { const m = c.val(); if (m && typeof m.text === "string" && now - (m.ts || 0) < CHAT.maxAge) out.push({ id: c.key, uid: m.uid, name: m.name, text: m.text, ts: m.ts }); }); S.chat = out; }, () => {});
  };
  const c = getCtx;
  const me = () => { const on = c().on; return { uid: on.authUid || null, name: on.name || null, signedIn: !!on.writable, dust: on.dust }; };
  const handsOff = () => S.humanAt != null && Date.now() - S.humanAt < SEAT.handsOffMs;
  const battle = () => { const b = c().battle.current; return b && (c().screen === "battle" || c().screen === "netbattle") ? b : null; };
  const mySide = (b) => (b.driver ? b.driver.mySide : 0);   // offline practice: she is side 0
  const iFight = (b) => !!b && (b.driver ? b.driver.mySide != null : !c().spectating());

  // her designated partner = her Mark's partner (the master plan: "logs in automatically just by being your partner")
  async function partner() {
    const uid = me().uid;
    if (!uid) return null;
    if (S.partner && Date.now() - S.partnerAt < 60000) return S.partner;
    const p = await net.get(`profiles/${uid}/mark/partner`).catch(() => null);
    if (typeof p !== "string" || !/^[A-Za-z0-9_-]{6,64}$/.test(p)) return null;
    const name = await net.get(`profiles/${p}/name`).catch(() => null);
    S.partner = p; S.partnerName = name ? String(name).slice(0, 24) : null; S.partnerAt = Date.now();
    return p;
  }
  // where he is, from the same pointers a friend's Join bar reads (online/together.js):
  //   room      his room — on the room screen, OR the room whose fight he's fighting (joining it sends her in to watch)
  //   watching  the fight he's watching / waiting at / fighting outside a room (watchWithPartner goes there)
  //   match     the fight he's in or watching, whichever way (so her side knows when she's already there)
  //   kind      together.js's plain answer · fighting / box: the details
  async function partnerState() {
    const p = await partner();
    if (!p) return { ok: false, reason: "no partner on your Mark" };
    const w = await whereabouts(p, net);
    const room = w.kind === "room" || w.kind === "roomfight" ? w.room : null;
    const watching = w.kind === "watch" || w.kind === "wait" || w.kind === "fight" ? w.match : null;
    return { ok: true, uid: p, name: S.partnerName, kind: w.kind, room, watching, match: w.match || null, fighting: w.kind === "fight" || w.kind === "roomfight", box: w.box || null };
  }

  function fightView() {
    const b = battle();
    if (!b) return null;
    if (S.moments.battle !== b) S.moments = { seq: 0, battle: b };
    const ms = mySide(b), fighting = iFight(b);
    const lead = !b.over ? leaderOf(b) : null;
    const names = b.sides.map((s) => s.name);
    const mm = momentsOf(b, S.moments);
    return {
      id: b.driver && b.driver.path ? b.driver.path.split("/").pop() : null,   // an online fight's id (practice: null)
      online: !!b.driver, fighting, mySide: fighting ? ms : null,
      t: Math.floor(b.t), callTime: b.callTime || CALL_TIME, left: Math.max(0, Math.ceil((b.callTime || CALL_TIME) - b.t)),
      over: !!b.over, winner: b.over && b.winner != null ? names[b.winner] : null,
      leading: lead && lead.winner != null ? { name: names[lead.winner], by: lead.by } : null,
      me: sideView(b, fighting ? ms : 0, fighting), them: sideView(b, fighting ? 1 - ms : 1, false),
      moments: mm.text, events: mm.data,
      bellIn: b.driver && b.driver.spec.startAt ? Math.max(0, Math.ceil((b.driver.spec.startAt - serverNow()) / 1000)) : 0,
    };
  }

  async function roomView() {
    const code = c().roomCode;
    if (!code || c().screen !== "room") return null;
    const room = await net.get(roomPath(code)).catch(() => null);
    if (!room) return { code, gone: true };
    const members = Object.entries(room.members || {}).map(([uid, m]) => ({ uid, name: m.name, team: m.team || null, host: uid === room.host, you: uid === me().uid }));
    let match = null;
    if (room.match) {
      const spec = await net.get(`${matchPath(room.match)}/spec`).catch(() => null);
      if (spec && !spec.void) match = { id: room.match, names: spec.names, teams: spec.teams, started: !!spec.startAt, bellIn: spec.startAt ? Math.max(0, Math.ceil((spec.startAt - serverNow()) / 1000)) : null, youFight: Object.values(spec.humans || {}).includes(me().uid),
          mySide: spec.humans && spec.humans[0] === me().uid ? 0 : spec.humans && spec.humans[1] === me().uid ? 1 : null };
    }
    return { code, host: (members.find((m) => m.host) || {}).name || null, mode: room.mode, youAreRival: room.rival === me().uid, members, match };
  }

  const api = {
    version: 1, game: "shatterproof",

    /** What her screen shows right now. */
    async look() {
      const x = c(), m = me();
      armChat(m.uid);
      const fight = fightView();
      const busy = x.screen === "signin" ? "signing in" : x.screen === "team" ? "the team picker is open" : x.markOpen ? "a Mark is open" : null;
      const out = {
        t: Date.now(), game: "shatterproof", screen: x.screen, where: WHERE[x.screen] || x.screen, busy,
        me: m, msSinceHuman: S.humanAt ? Date.now() - S.humanAt : null, handsOff: handsOff(),
        team: (x.team || []).slice(0, TEAM_SIZE), tomes: x.tomes || null,
        room: await roomView(), fight,
        result: x.screen === "results" && x.result ? { won: !!x.result.won, why: x.result.why, by: x.result.by, time: Math.round(x.result.stats.time) } : null,
        chat: x.screen === "netbattle" && fight && !fight.fighting ? S.chat.slice(-10).map((q) => ({ ...q, mine: q.uid === m.uid })) : [],
        betsOpen,
        // the Sky Box she's in (when she's watching): its kind, whether she's waiting, who's in it with her (uids)
        skybox: boxView(),
      };
      return out;
    },

    /** The places she can go, by id. kind: "landmark" (goTo) · "watch" (her side watches that league, bout after bout)
     *  · "wait" (waitForPartner: she waits for her partner in a Sky Box). The Control Center draws one section per kind. */
    places() {
      return [
        { id: "title", label: "the title screen", kind: "landmark" }, { id: "online", label: "the League & friends hub", kind: "landmark" },
        { id: "league", label: "the League board (watch, follow the table)", kind: "landmark" }, { id: "rulebook", label: "the rulebook", kind: "landmark" },
        { id: "watch-league", label: "The League", kind: "watch" }, { id: "watch-circuit", label: "The Circuit", kind: "watch" },
        { id: "watch-contenders", label: "The Contenders", kind: "watch" },
        { id: "wait-public", label: "Public Sky Box", kind: "wait" }, { id: "wait-private", label: "Private Sky Box", kind: "wait" },
      ];
    },
    goTo(id) {
      const x = c();
      if (battle() && !battle().over) return { ok: false, reason: "you're in a fight — leave() first" };
      const to = { title: "title", online: "online", league: "hourly", rulebook: "how", signin: "signin" }[id];   // signin: her seat signs in (not listed in places)
      if (!to) return { ok: false, reason: `don't know "${id}" — try places()` };
      if (to === "hourly" && !me().uid) return { ok: false, reason: "sign in first" };
      if (to === "signin" && me().signedIn) return { ok: false, reason: "already signed in" };
      x.setScreen(to);
      return { ok: true, to: id };
    },

    /** Every being she could pick, with what makes each one itself. */
    beings() {
      return LINE_IDS.map((id) => {
        const L = LINES[id];
        return {
          line: id, name: L.lord, card: L.name, kin: L.kin, ilk: L.ilk, attack: L.lyt === "lunge" ? "melee" : "ranged",
          tiers: Object.fromEntries(TIER_ORDER.map((t) => [t, { lyf: L.tiers[t].lyf, lyt: L.tiers[t].lyt }])),
          impera: L.impera.map((a) => ARTS[a].name), uhlti: ARTS[L.uhlti] ? ARTS[L.uhlti].name : null,
        };
      });
    },
    styles() { return STYLE_IDS.map((id) => ({ id, name: STYLES[id].name, blurb: STYLES[id].blurb })); },
    tomeList() { return TOME_IDS.map((id) => ({ id, name: ARTS[id].name, khao: ARTS[id].khao })); },

    /** Her three (and optionally her three Tomes). Kept in her browser like anyone's. */
    setTeam(lines, tomes) {
      const t = Array.isArray(lines) ? lines.map(String) : [];
      if (t.length !== TEAM_SIZE || new Set(t).size !== TEAM_SIZE || !t.every((l) => LINES[l])) return { ok: false, reason: `pick ${TEAM_SIZE} different beings by line id — see beings()` };
      let tm = null;
      if (tomes != null) {
        tm = Array.isArray(tomes) ? tomes.map(String) : [];
        if (tm.length !== TOME_SLOTS || new Set(tm).size !== TOME_SLOTS || !tm.every((id) => TOME_IDS.includes(id))) return { ok: false, reason: `pick ${TOME_SLOTS} different Tomes — see tomeList()` };
      }
      if (battle() && !battle().over) return { ok: false, reason: "not mid-fight" };
      c().setTeam(t); if (tm) c().setTomes(tm);
      try { const k = "shatterproof", cur = JSON.parse(localStorage.getItem(k) || "{}"); localStorage.setItem(k, JSON.stringify({ ...cur, team: t, ...(tm ? { tomes: tm } : {}) })); } catch { /* a private window: just this visit */ }
      return { ok: true, team: t.map(lordOf), tomes: tm };
    },

    /** Where her partner is (his room, the fight he's watching). */
    partnerState,
    /** Go to her partner: his room if he's in one. */
    async joinPartner() {
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      if (battle() && !battle().over) return { ok: false, reason: "you're in a fight — leave() first" };
      const ps = await partnerState();
      if (!ps.ok) return ps;
      const code = ps.room || (c().on.partyId ? await net.get(`${BASE}/partyRooms/${c().on.partyId}`).catch(() => null) : null);
      if (!code) return { ok: false, reason: ps.watching ? "he isn't in a room — he's watching a fight (watchWithPartner())" : "he isn't in a room right now" };
      if ((c().team || []).length !== TEAM_SIZE) return { ok: false, reason: "pick your three first (setTeam)" };
      const r = await joinRoom(net, { code, uid: me().uid, name: me().name });
      if (!r.ok) return r;
      c().setRoomCode(r.code); c().setScreen("room");
      return { ok: true, room: r.code };
    },
    /** Watch the fight her partner is watching (and sit with him in his Sky Box, when there is one). */
    async watchWithPartner() {
      if (battle() && !battle().over && iFight(battle())) return { ok: false, reason: "you're in a fight" };
      const ps = await partnerState();
      if (!ps.ok) return ps;
      if (!ps.watching) return { ok: false, reason: "he isn't watching a fight right now" };
      // sitWith answers with a toast, not a value: whether the fight opened is what the screen says after
      if (typeof c().sitWith === "function") await c().sitWith(ps.uid, ps.name);
      else await c().watchMatch(ps.watching, "hourly");
      await new Promise((res) => setTimeout(res, 60));
      return c().screen === "netbattle" ? { ok: true, watching: ps.watching } : { ok: false, reason: "couldn't open his fight (it may have ended)" };
    },
    async leaveRoom() {
      const code = c().roomCode;
      if (!code) return { ok: false, reason: "not in a room" };
      const room = await net.get(roomPath(code)).catch(() => null);
      await leaveRoom(net, { code, uid: me().uid, room });
      c().setRoomCode(null); c().setScreen("online");
      return { ok: true };
    },

    /** A practice fight against the rival, her side on Auto from the bell. */
    practice() {
      const x = c();
      if (battle() && !battle().over) return { ok: false, reason: "already in a fight" };
      if ((x.team || []).length !== TEAM_SIZE) return { ok: false, reason: "pick your three first (setTeam)" };
      x.start(x.team, false, x.tomes);
      const b = x.battle.current;
      if (b) issue(b, 0, { type: "auto", on: true });
      return { ok: true, vs: b ? b.sides[1].roster.map((r) => lordOf(r.line)) : null };
    },
    /** Watch a fight by id (a League bout, or a room's match). */
    async watch(id) {
      if (battle() && !battle().over && iFight(battle())) return { ok: false, reason: "you're in a fight" };
      if (typeof id !== "string" || !/^[A-Za-z0-9_]{3,40}$/.test(id)) return { ok: false, reason: "a fight id from league() or look()" };
      const opened = await c().watchMatch(id, "hourly");   // false = it hadn't started (it waits ~30 s for a bell at most)
      return opened === false ? { ok: false, reason: "that fight hasn't started — come back nearer its bell" } : { ok: true };
    },
    /** Leave whatever she's in (a fight, the results, a room) — one step back. */
    async leave() {
      const x = c();
      if (x.screen === "netbattle") { x.leaveNet(); return { ok: true }; }
      if (x.screen === "battle" || x.screen === "results") { x.setScreen("title"); return { ok: true }; }
      if (x.screen === "room") return api.leaveRoom();
      if (x.screen !== "title") { x.setScreen("title"); return { ok: true }; }
      return { ok: false, reason: "already on the title screen" };
    },

    /**
     * AN ORDER for her side, mid-fight. { do, ... }:
     *   auto {on}        hand the side to the brain (or take it back)
     *   style {style}    her game plan from now on (styles())
     *   ascend           Transcend the being out there
     *   switch {line}    swap in a benched being
     *   revive {line, to:"hand"|"field"}   resurrect a ruined being
     *   send {line}      after a ruin: who goes next
     *   impera {i}  ·  uhlti  ·  tome {slot}   an Art (the brain aims it)
     * Checked here first so she hears WHY it can't happen ("Lord opens at 2:30").
     */
    order(o) {
      const b = battle();
      if (!b) return { ok: false, reason: "not in a fight" };
      if (!iFight(b)) return { ok: false, reason: "you're watching this one" };
      if (b.over) return { ok: false, reason: "the fight is over" };
      const what = String((o && o.do) || "").toLowerCase();
      // the plan (auto + style) can be set before the bell; everything else waits for it
      if (b.driver && b.driver.spec.startAt && serverNow() < b.driver.spec.startAt && what !== "auto" && what !== "style") return { ok: false, reason: "the bell hasn't rung — set your plan (auto, style) and wait" };
      if (handsOff()) return { ok: false, reason: "a human has the wheel — hands off for a bit" };
      const now = Date.now();
      // the gap is for ACTIONS; the plan (auto + style) is set in one breath (review 2026-10-17: the gap ate her style every fight)
      if (what !== "auto" && what !== "style" && now - S.lastOrder < SEAT.orderGap) return { ok: false, reason: "slow down — one order at a time" };
      const side = mySide(b), s = b.sides[side], f = s.active >= 0 ? s.roster[s.active] : null;
      const find = (line) => s.roster.find((r) => r.line === line || r.idx === line || lordOf(r.line).toLowerCase() === String(line || "").toLowerCase());
      const go = (cmd, said) => { const ok = issue(b, side, cmd); if (ok) S.lastOrder = now; return ok ? { ok: true, did: said } : { ok: false, reason: "the fight said no" }; };
      switch (what) {
        case "auto": return go({ type: "auto", on: o.on !== false }, o.on !== false ? "auto on" : "auto off");
        case "style": {
          const id = String(o.style || "").toLowerCase();
          if (!STYLE_IDS.includes(id)) return { ok: false, reason: `plans are ${STYLE_IDS.join(", ")}` };   // a list check: "constructor" is not a plan
          if ((s.style || "balanced") === id) return { ok: false, reason: "that's already the plan" };
          return go({ type: "style", id: id === "balanced" ? null : id }, `plan: ${STYLES[id].name}`);
        }
        case "ascend": {
          if (!f) return { ok: false, reason: "nobody's out there" };
          if (f.tier === "lord") return { ok: false, reason: `${lordOf(f.line)} is already a Lord` };
          const to = TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1], wait = transcendWait(b, to);
          if (wait > 0) return { ok: false, reason: `${to === "lord" ? "Lord" : "Champion"} opens in ${Math.ceil(wait)} s` };
          if (s.khao < TRANSCEND_KHAO[to]) return { ok: false, reason: `needs ${TRANSCEND_KHAO[to]} Khao (you have ${Math.floor(s.khao)})` };
          return go({ type: "transcend" }, `${lordOf(f.line)} → ${to}`);
        }
        case "switch": {
          const r = find(o.line);
          if (!f) return { ok: false, reason: "nobody's out there to switch" };
          if (!r || r.ruined || r === f) return { ok: false, reason: "pick a benched being who's still standing" };
          if (s.swapLock > 0) return { ok: false, reason: "you just switched — wait a moment" };
          if (s.khao < SWAP_KHAO[r.tier]) return { ok: false, reason: `needs ${SWAP_KHAO[r.tier]} Khao` };
          return go({ type: "swap", idx: r.idx }, `switch to ${lordOf(r.line)}`);
        }
        case "revive": {
          const r = find(o.line);
          if (!r || !r.ruined) return { ok: false, reason: "pick a ruined being" };
          if (r.resUsed >= RES_TALLY[r.tier]) return { ok: false, reason: `${lordOf(r.line)} already came back once` };
          if (s.khao < RES_FARE[r.tier]) return { ok: false, reason: `needs ${RES_FARE[r.tier]} Khao` };
          return go({ type: "resurrect", idx: r.idx, to: o.to === "field" ? "field" : "hand" }, `bring back ${lordOf(r.line)}`);
        }
        case "send": {
          const r = find(o.line);
          if (s.active >= 0) return { ok: false, reason: "someone's already out there" };
          if (!r || r.ruined) return { ok: false, reason: "pick someone still standing" };
          return go({ type: "send", idx: r.idx }, `send ${lordOf(r.line)}`);
        }
        case "impera": {
          const i = Number(o.i);
          if (!f || !(i === 0 || i === 1 || i === 2)) return { ok: false, reason: "impera i is 0, 1 or 2" };
          if (!imperaOpen(i, f.tier)) return { ok: false, reason: `that Impera opens at a higher tier` };
          return go({ type: "impera", i }, ARTS[LINES[f.line].impera[i]].name);
        }
        case "uhlti":
          if (!f || f.tier !== "lord") return { ok: false, reason: "only a Lord has a U'hlti" };
          return go({ type: "uhlti" }, ARTS[LINES[f.line].uhlti].name);
        case "tome": {
          const slot = Number(o.slot);
          if (!f || !s.tomes[slot]) return { ok: false, reason: `tome slot is 0..${TOME_SLOTS - 1}` };
          if (s.tomes[slot].cd > 0) return { ok: false, reason: "that Tome is cooling down" };
          return go({ type: "tome", slot }, ARTS[s.tomes[slot].id] ? ARTS[s.tomes[slot].id].name : "a Tome");
        }
        default: return { ok: false, reason: "orders: auto, style, ascend, switch, revive, send, impera, uhlti, tome" };
      }
    },

    /** Say a call out loud: it floats under her name on every screen in the fight. */
    call(text) {
      const b = battle();
      if (!b || !iFight(b)) return { ok: false, reason: "calls are for your own fights" };
      return shout(b, mySide(b), cleanText(text, CALL.max));
    },

    /** The world chat — only while watching a fight (the game's own rule). */
    async say(text) {
      const b = battle(), m = me();
      if (!m.signedIn) return { ok: false, reason: "sign in first" };
      if (!b || !b.driver || iFight(b)) return { ok: false, reason: "the world chat is only open while you watch an online fight" };
      const now = Date.now();
      if (now - S.lastSay < SEAT.sayGap) return { ok: false, reason: "slow down — one line at a time" };
      const t = fitText(text, CHAT.max);
      if (!t) return { ok: false, reason: "nothing to say" };
      const cut = t.length < cleanText(text, 100000).length;
      S.lastSay = now;
      await push(ref(db, `zones/${WORLD_ZONE}/chat`), { uid: m.uid, name: String(m.name || "Unclaimed").slice(0, 24), text: t, ts: serverNow() });
      // …and on her sign, for SIGN.chatShow seconds, for everyone in her box (the human chat box does exactly this)
      const onSign = !!BOX.inside;
      if (onSign) sayOnSign(t);
      return { ok: true, said: t, onSign, cut };
    },

    /** Say something to the people in HER Sky Box, on her sign only (SIGN.chatShow seconds). NOTHING goes to the world chat:
     *  that is global, and a cheer at the box is for the box (caP'n 2026-10-21's "signs we can hold up"). */
    signSay(text) {
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      if (!BOX.inside) return { ok: false, reason: "you're not in the Sky Box" };
      if (Date.now() - S.lastSign < SEAT.signSayGap) return { ok: false, reason: "slow down — one sign at a time" };
      const t = fitText(text, SIGN.max);
      if (!t) return { ok: false, reason: "nothing to say" };
      S.lastSign = Date.now();
      sayOnSign(t);
      return { ok: true, said: t, cut: t.length < cleanText(text, 100000).length };
    },

    /** An emote in the Sky Box: "wave" or "cheer" (data/skybox.js EMOTES). Everyone in her box sees the pose. */
    emote(id) {
      const e = EMOTES.find((x) => x.id === id);
      if (!e) return { ok: false, reason: `emotes are ${EMOTES.map((x) => `"${x.id}"`).join(" or ")}` };
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      if (!BOX.inside) return { ok: false, reason: "you're not in the Sky Box" };
      if (Date.now() - S.lastEmote < SEAT.emoteGap) return { ok: false, reason: "easy — one emote at a time" };
      S.lastEmote = Date.now();
      boxEmote(e.id);
      return { ok: true, emote: e.id, dur: e.dur };
    },

    /** Her own sign: write it (text; "" clears) and raise or lower it (up). board: a FREE board id (parchment · sun · mint).
     *  Text longer than the sign is cut at a word (`cut: true`). Everyone else sees it solid; it's see-through for her. */
    sign(text, up = true, board = null) {
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      let t = BOX.me.sign, cut = false;
      if (text != null) { t = fitText(text, SIGN.max); cut = t.length < cleanText(text, 100000).length; setSign(t); }
      if (board != null) {
        if (!SIGN_BOARDS.some((b) => b.id === board && b.free)) return { ok: false, reason: `boards you can use: ${SIGN_BOARDS.filter((b) => b.free).map((b) => b.id).join(", ")}` };
        setBoard(board);
      }
      toggleSignUp(!!up && !!t);
      return { ok: true, sign: t, up: !!BOX.me.signUp, board: BOX.me.board, cut };
    },

    /** Pick the camera of the fight on screen: "sky" puts her BODY in the Sky Box (the same V key everyone has), "front" the front row,
     *  "broadcast" the TV view. Only while a fight is up (the camera isn't mounted otherwise). */
    view(v) {
      if (!["broadcast", "front", "sky"].includes(v)) return { ok: false, reason: 'views are "broadcast", "front" or "sky"' };
      if (!battle()) return { ok: false, reason: "no fight on screen" };
      if (typeof BOX.ui.setView !== "function") return { ok: false, reason: "the camera isn't up yet — try again in a second" };
      BOX.ui.setView(v);
      return { ok: true, view: v };
    },

    /** Sit down in the nearest free seat of the Sky Box (on=true) or get up (on=false). */
    seat(on = true) {
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      if (!BOX.inside) return { ok: false, reason: "you're not in the Sky Box" };
      if (!on) { standUp(); return { ok: true, seat: BOX.me.seat }; }
      if (BOX.me.seat >= 0) return { ok: true, seat: BOX.me.seat, already: true };
      return sitNearest() ? { ok: true, seat: BOX.me.seat } : { ok: false, reason: "every seat is taken" };
    },

    /** The League right now: what's on, and what's coming (with each fight's id, for watch/predict/bet). */
    async league() {
      if (!me().uid) return { ok: false, reason: "sign in first" };
      if (S.league && Date.now() - S.leagueAt < SEAT.leagueCacheMs) return S.league;
      if (!S.leagueBusy) S.leagueBusy = (async () => {
        const st = await loadStage(net, serverNow());
        const myKey = me().uid ? chKey(me().uid) : null;   // a Challenger's bout is hers: "mine" tells her side which fight to walk into
        const bout = (f) => ({
          id: f.id, phase: f.phase, kind: f.k || null, div: f.dv === "k" ? "contenders" : "circuit",   // div: cups and Apocalypse are Circuit nights
          stage: laneOf(f) === 1 ? "contenders" : "circuit",   // V3: which of the two stages it's on
          mine: !!(myKey && ((f.A && f.A.key === myKey) || (f.B && f.B.key === myKey))),
          a: f.A ? { name: f.A.name, team: (f.A.team || []).map(lordOf) } : null, b: f.B ? { name: f.B.name, team: (f.B.team || []).map(lordOf) } : null,
          bellIn: f.startAt ? Math.round((f.startAt - serverNow()) / 1000) : f.estAt ? Math.round((f.estAt - serverNow()) / 1000) : null, guess: !f.startAt,
        });
        const done = st.cur ? st.cur.bouts.filter((f) => f.phase === "done").slice(-3).map((f) => ({ ...bout(f), winner: f.winner != null ? (f.winner === 0 ? f.A.name : f.B.name) : null })) : [];
        const ob = st.onBy || [st.on, null];
        S.league = { ok: true, on: st.on ? bout(st.on) : null, onStage: { circuit: ob[0] ? bout(ob[0]) : null, contenders: ob[1] ? bout(ob[1]) : null }, up: [...st.up.slice(0, 6), ...st.up.slice(6).filter((f) => myKey && ((f.A && f.A.key === myKey) || (f.B && f.B.key === myKey)))].map(bout), justFought: done };   // (her own bouts always listed)
        S.leagueAt = Date.now();
      })().catch((e) => { S.league = { ok: false, reason: String((e && e.message) || e).slice(0, 120) }; S.leagueAt = Date.now(); }).finally(() => { S.leagueBusy = null; });
      await S.leagueBusy;
      return S.league;
    },
    /**
     * HER LEAGUE CAREER (caP'n 2026-10-21: "she can actually chase something more meaningful … when she wins … proper
     * recognition"). She's a Challenger like any player (online/challengers.js) — the same three verbs his screen uses:
     *   joinLeague()          once, forever — official, in the Contenders from the next card
     *   imIn(on)              "I'm in": on every card that goes up while it's on (her three from setTeam); her side
     *                         calls it again every few minutes to keep it fresh (READY_EVERY), and imIn(false) to stop
     *   checkIn(cup)          tonight's check-in (open any time from the week's start until its 1pm / 6pm card): from V3 "n" = ALL
     *                         FIVE of a cup night · "week" = every one still open this week ·
     *                         Saturday "s" Salvation / "f" the Finale · "l" the Losers' Cup (5:40–5:50pm) · before V3 "k"/"c"/"x"
     *                         — only in the 10 minutes before its first card goes up (career().cupsTonight lists them)
     *   career()              where she stands: member? her place, record, rating, her Salvation points + the line,
     *                         tonight's check-in windows
     */
    async joinLeague() {
      const m = me();
      if (!m.uid || !m.signedIn) return { ok: false, reason: "sign in first" };
      return joinLeague(net, { uid: m.uid, name: m.name, partner: !!(await partner()), now: serverNow() });
    },
    async imIn(on = true) {
      const m = me();
      if (!m.uid || !m.signedIn) return { ok: false, reason: "sign in first" };
      const members = await membersOf(net);
      if (!members.has(chKey(m.uid))) return { ok: false, reason: "join the League first (joinLeague)" };
      return setReady(net, { uid: m.uid, on: !!on, team: (c().team || []).slice(0, TEAM_SIZE), tomes: c().tomes || null, name: m.name, partner: !!(await partner()), now: serverNow() });
    },
    async checkIn(cup) {
      const m = me();
      if (!m.uid || !m.signedIn) return { ok: false, reason: "sign in first" };
      const day0 = racingDayOf(serverNow());
      if (cup === "week") {   // every check-in still open this week, in one go (caP'n 2026-10-22: "you can register early")
        const out = [];
        for (let d = day0; d <= saturdayOf(weekOfDay(day0)); d++) for (const c2 of checkInsOfDay(d)) out.push({ cup: c2, day: d, ...(await checkIn(net, { c: c2, day: d, uid: m.uid, team: (c().team || []).slice(0, TEAM_SIZE), tomes: c().tomes || null, name: m.name, partner: !!(await partner()), now: serverNow() })) });
        return { ok: out.some((x) => x.ok), entered: out.filter((x) => x.ok).map((x) => `${x.cup}_${x.day}`) };
      }
      const tonight = checkInsOfDay(day0);
      if (!tonight.includes(cup)) return { ok: false, reason: tonight.length ? `tonight's check-ins: ${tonight.join(", ")} (or "week")` : "no cups to check in for tonight" };
      const members = await membersOf(net);
      if (!members.has(chKey(m.uid))) return { ok: false, reason: "join the League first (joinLeague)" };
      const now = serverNow();
      return checkIn(net, { c: cup, day: racingDayOf(now), uid: m.uid, team: (c().team || []).slice(0, TEAM_SIZE), tomes: c().tomes || null, name: m.name, partner: !!(await partner()), now });
    },
    async career() {
      const m = me();
      if (!m.uid) return { ok: false, reason: "sign in first" };
      const now = serverNow(), key = chKey(m.uid), members = await membersOf(net);
      if (!members.has(key)) return { ok: true, member: false };
      const day = racingDayOf(now), v3 = dayIsV3(day);
      const w = weekOf(now), base = withMembers(await leagueOf(net, w) || { circuit: [], contenders: [] }, members, hourOf(now) + 1);
      const rows = await ledgerOf(net).rows(w), league = leagueOn(base, rows, day), St = standings(rows, league.elo || null), s = statOf(St, key);
      const div = league.circuit.includes(key) ? "circuit" : "contenders", rec = v3 ? divRec(div === "circuit" ? "c" : "k") : REC.week, [won, lost] = rec(s);
      const place = rankKeys(league[div], St, rec).indexOf(key) + 1;
      const cups = checkInsOfDay(day).map((cup) => { const win = checkInWindow(cup, day); return { cup, name: cup === "n" ? "tonight's Qualifiers and cups" : CUPS[cup].name, opens: win ? Math.round((win.open - now) / 1000) : null, closes: win ? Math.round((win.close - now) / 1000) : null }; });
      // V3: her Salvation points (Saturday's King's Cup is drawn off them) and the line she has to reach
      let points = null;
      if (v3) { const P = salvationOf(base, rows, base.elo || null), me2 = P.get(key); points = { score: me2 ? Math.round(me2.score) : 0, circuit: me2 ? Math.round(me2.cp) : 0, contenders: me2 ? Math.round(me2.kp) : 0, line: salvationLine(P, base) }; }
      return { ok: true, member: true, division: div, place, week: { won, lost }, qualified: won + lost >= QUALIFY, rating: Math.round(s.elo), points, escaped: (league.escaped || []).includes(key), cupsTonight: cups };
    },
    /** Who won a fight (null while it's still going or not decided). */
    async result(id) {
      if (typeof id !== "string" || !/^[A-Za-z0-9_]{3,40}$/.test(id)) return { ok: false, reason: "a fight id" };
      const [r, spec] = await Promise.all([matchResult(net, matchPath(id), serverNow()).catch(() => null), net.get(`${matchPath(id)}/spec`).catch(() => null)]);
      if (!r) return { ok: true, done: false };
      if (r.void) return { ok: true, done: true, void: true };
      return { ok: true, done: true, winner: spec && spec.names ? spec.names[r.winner] : r.winner, why: r.why || null };
    },
    /** A Mini Dust bet — ONLY when caP'n opened it (&bets=1). */
    async bet(id, side) {
      if (!betsOpen) return { ok: false, reason: "betting isn't open to you yet" };
      const m = me();
      const spec = await net.get(`${matchPath(id)}/spec`).catch(() => null);
      if (!spec || spec.void) return { ok: false, reason: "no such fight (yet)" };
      return placeBet(net, { poolPath: `${matchPath(id)}/bets`, uid: m.uid, name: m.name, side: Number(side), amount: BET_MIN, lockAt: spec.startAt ?? null, now: serverNow, keys: spec.keys || null, fighters: Object.values(spec.humans || {}).filter(Boolean) });
    },

    /**
     * WAIT FOR PARTNER (caP'n 2026-10-18): "she goes to the nearest waiting area … in Shatterproof's case it's the
     * public skybox … or wait for partner (private) … she would go load into a skybox and make a private one for us <3".
     * kind "public" | "private" → she watches the League bout on now from a Sky Box of that kind, takes a seat, and her
     * `where` says she's WAITING (friends see it on her Mark: "Waiting for you in the Sky Box"). null → stop waiting.
     * Between bouts she's off the box (no fight = no box); her side calls this again for the next one.
     */
    async waitForPartner(kind) {
      if (kind == null || kind === false) { setWaiting(false); return { ok: true, waiting: false }; }
      if (!BOX_MODES.includes(kind)) return { ok: false, reason: `kind is ${BOX_MODES.map((k) => `"${k}"`).join(" or ")}` };
      if (!me().signedIn) return { ok: false, reason: "sign in first" };
      const b0 = battle();
      if (b0 && !b0.over && iFight(b0)) return { ok: false, reason: "you're in a fight" };
      if (BOX.mode !== kind || isGuest()) setBoxMode(kind);
      // the waiting flag goes up only once she's really at a fight (review 2026-10-18: a failed try left it on)
      const sit = () => setTimeout(() => { if (BOX.inside && BOX.me.seat < 0) sitNearest(); }, 1500);
      if (c().screen === "netbattle" && b0 && b0.driver && !b0.over) {   // already watching one: wait right here
        if (BOX.ui.setView) BOX.ui.setView("sky");
        setWaiting(true); sit();
        return { ok: true, box: kind, watching: b0.driver.path.split("/").pop() };
      }
      const lg = await api.league();
      if (!lg.ok) return { ok: false, reason: lg.reason || "couldn't read the League" };
      const done = b0 && b0.over && b0.driver ? b0.driver.path.split("/").pop() : null;   // the bout she just saw end: never again
      const on = lg.on && lg.on.a && lg.on.id !== done ? lg.on : null;
      const soon = (lg.up || []).filter((f) => f.a && f.b && f.id !== done && f.bellIn != null && f.bellIn <= 25).sort((x, y) => x.bellIn - y.bellIn)[0];
      const pick = on || soon;
      if (!pick) {
        setWaiting(false);
        const next = (lg.up || []).filter((f) => f.bellIn != null && f.bellIn > 0).sort((x, y) => x.bellIn - y.bellIn)[0];
        return { ok: false, reason: "no League bout on right now", bellIn: next ? next.bellIn : null };
      }
      BOX.wantView = "sky";
      const opened = await c().watchMatch(pick.id, "hourly");
      if (opened === false) { BOX.wantView = null; setWaiting(false); return { ok: false, reason: "that bout hasn't started", bellIn: pick.bellIn }; }
      setWaiting(true); sit();
      return { ok: true, box: kind, watching: pick.id };
    },

    /**
     * FRIEND REQUESTS TO HER (caP'n 2026-10-20: "players can add people's partners as friends"). Who asked her,
     * and whether each one is already her partner's friend (`partnersFriend` — her side takes those on its own;
     * everyone else waits for her partner's OK in the Control Center). Her partner himself is the bond's job.
     */
    async friendRequests() {
      const m = me();
      if (!m.uid) return { ok: false, reason: "sign in first" };
      const edges = (await net.get(`profiles/${m.uid}/friends`).catch(() => null)) || {};
      const p = await partner();
      const asked = Object.entries(edges).filter(([u, e]) => e && e.status === "in" && /^[A-Za-z0-9_-]{6,64}$/.test(u)).slice(0, 20);
      const requests = [];
      for (const [u, e] of asked) {
        const pe = p && u !== p ? await net.get(`profiles/${p}/friends/${u}`).catch(() => null) : null;
        requests.push({ uid: u, name: cleanText(e.name, 24) || "Someone", isPartner: u === p, partnersFriend: u !== p && partnerRequestStep({ partnerEdgeToThem: pe }) === "accept" });
      }
      return { ok: true, requests };
    },
    /** Yes or no to one request (only a request that's really there). */
    async answerFriend(uid, yes) {
      const m = me();
      if (!m.uid) return { ok: false, reason: "sign in first" };
      if (typeof uid !== "string" || !/^[A-Za-z0-9_-]{6,64}$/.test(uid)) return { ok: false, reason: "a uid from friendRequests()" };
      const e = await net.get(`profiles/${m.uid}/friends/${uid}`).catch(() => null);
      if (!e || e.status !== "in") return { ok: false, reason: "no request from them" };
      const r = yes ? await acceptRequest(m.uid, m.name, uid, e.name) : await declineRequest(m.uid, uid);
      return r.ok ? { ok: true, did: yes ? "accepted" : "declined" } : { ok: false, reason: r.error };
    },

    /** Going home: a one-line note of what she was doing, for her side to save. */
    pause() {
      const x = c(), b = battle();
      let note = `on ${WHERE[x.screen] || x.screen}`;
      if (BOX.waiting) setWaiting(false);   // going home: she's no longer waiting (her where goes with her window anyway)
      if (b && !b.over) { const v = fightView(); note = v.fighting ? `mid-fight vs ${v.them.name}, ${Math.floor(v.t / 60)}:${String(v.t % 60).padStart(2, "0")} in` : `watching ${v.me.name} vs ${v.them.name}`; }
      else if (x.screen === "room" && x.roomCode) note = `in room ${x.roomCode}`;
      return { ok: true, note, team: (x.team || []).map(lordOf) };
    },
  };
  return {
    api,
    human: () => { S.humanAt = Date.now(); },
    close: () => { if (typeof offChat === "function") offChat(); },
  };
}
